import { Component, OnInit } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import {
  PolicyClient,
  PolicySearchResultsResponse,
  SearchCategory,
  SearchSortDirection,
  SearchSortField
} from '@benefitsui/app/api/benefits-clients';
import { UserContextService } from '@benefitsui/app/services/user-context/user-context.service';
import { UserContext } from '@benefitsui/app/services/user-context/user-context.types';
import { BehaviorSubject, combineLatest, interval, merge } from 'rxjs';
import { debounce, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { GroupsSearchParameters, PolicyData, PolicyInfo } from './list-policies-page.types';
import { AuthenticationService } from '@benefitsui/app/services/auth/authentication.service';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import { DataTableHeaderOption } from '@dxo/ng-swift-ui';
import { environment } from '@benefitsui/environments/environment';

@Component({
  selector: 'app-list-policies-page',
  templateUrl: './list-policies-page.component.html',
  styleUrls: ['./list-policies-page.component.scss'],
  standalone: false
})
export class ListPoliciesPageComponent implements OnInit {
  PolicyTitle = 'Search for policies';
  GroupTitle = 'Search for groups';
  PolicyHolder = 'Policy holder';
  PolicyBodyHeader = 'All policies';
  PolicyNumber = 'Policy number';
  GroupName = 'Group name';
  GroupNumber = 'Group number';
  GroupBodyHeader = 'Groups';
  AlertButtonText = 'Visit Go';
  AlertButtonLink = 'https://benefits.symetra.com/BenefitsPortal/EmployerPolicy'
  AlertText1 = "Can't find your group?";
  AlertText2 = 'Some groups will still be managed in GO, as we work toward moving all coverage management here — the new GoSymetra experience.'
  AlertText3 = 'Questions?';
  AlertText4 = "Don't hesitate to";
  AlertText5 = 'contact us!';
  AlertText5Link = 'mailto:benefitsportalsecurity@symetra.com?subject=Benefits Portal Customer Support';
  EmptyResultsText = 'There are no groups to display.';

  hasPolicies$ = new BehaviorSubject<boolean>(false);
  userFullName: string;
  userContext: UserContext;
  bodyHeader = this.PolicyBodyHeader;
  isPolicy = true;
  pageTitle = this.PolicyTitle;
  tableName = this.PolicyHolder;
  tableNumber = this.PolicyNumber;
  hasTermsRecords = false;
  termsConditionsAccepted = false;
  electronicConsentAccepted = false;
  loading = true;
  showPopUpPage = false;
  tokenForOverview = '';
  isSponsor = false;
  isGoUser = false
  isSymEmpl = false;
  isBroker = false;

  constructor(
    private router: Router,
    private userContextService: UserContextService,
    private policyClient: PolicyClient,
    private authService: AuthenticationService,
    private fb: UntypedFormBuilder
  ) {
    this.userContext = this.userContextService.get();
    this.userFullName = this.userContext.currentUserFullName;
    this.formGroup = this.fb.group({
      groupName: Defaults.stringEmpty,
      groupNumber: Defaults.stringEmpty
    });
  }

  policyData: PolicyData = {
    policies: [],
    totalResults: 0
  };

  formGroup: FormGroup;
  searchParameter: GroupsSearchParameters = {
    groupName: undefined,
    groupNumber: undefined,
    page: 1,
    pageSize: 10,
    sortField: SearchSortField.GroupName,
    sortDirection: SearchSortDirection.Ascending
  };
  tableHeaders: DataTableHeaderOption[] = [
    { text: 'Group name', key: 'groupName', width: '60%' },
    { text: 'Group number', key: 'groupNumber', width: '40%' }
  ];
  groupName$ = new BehaviorSubject<string | null>(Defaults.stringEmpty);
  groupNumber$ = new BehaviorSubject<string | null>(Defaults.stringEmpty);
  private pageSize$ = new BehaviorSubject<number>(10);
  private page$ = new BehaviorSubject<number>(1);
  private sortDirection$ = new BehaviorSubject<SearchSortDirection>(SearchSortDirection.Ascending);
  private sortField$ = new BehaviorSubject<SearchSortField>(SearchSortField.GroupName);

  policiesList$ = combineLatest([
    this.page$,
    this.pageSize$.pipe(distinctUntilChanged()),
    this.sortDirection$.pipe(distinctUntilChanged()),
    this.sortField$.pipe(distinctUntilChanged())
  ]).pipe(
    switchMap(([page, pageSize, sortDirection, sortField]) => {
      this.searchParameter = {
        ...this.searchParameter,
        page,
        pageSize,
        sortDirection,
        sortField,
        groupName: this.groupName$.value ?? Defaults.stringEmpty,
        groupNumber: this.groupNumber$.value ?? Defaults.stringEmpty
      };
      return this.policyClient
        .search({
          category: SearchCategory.GroupId,
          criteria: '',
          groupName: this.searchParameter.groupName,
          groupNumber: this.searchParameter.groupNumber,
          page: this.searchParameter.page,
          pageSize: this.searchParameter.pageSize,
          sortField: this.searchParameter.sortField,
          sortDirection: this.searchParameter.sortDirection
        })
        .pipe(map((x) => x?.data));
    }),
    map((x) => this.transformData(x))
  );

  ngOnInit(): void {
    this.checkGOUser(this.userContext);
    if(this.isGoUser){
        this.routeToGo();
    }
    else{
      this.isGroupsPage(this.userContext);
      this.policiesList$.subscribe();
      if (this.formGroup) {
        const groupName = this.formGroup.get('groupName');
        const groupNumber = this.formGroup.get('groupNumber');
        groupName?.valueChanges.subscribe((value) => {
          this.groupName$.next(value);
        });
        groupNumber?.valueChanges.subscribe((value) => {
          this.groupNumber$.next(value);
        });
      }
      merge(this.groupName$, this.groupNumber$)
        .pipe(debounce(() => interval(250)))
        .subscribe(() => this.page$.next(1));
    }
  }

  showTermsConditionsElectronicPopup() {
    if (this.isSymEmpl || this.isBroker) {
      this.showPopUpPage = false;
    } else {
      this.hasTermsRecords = this.userContext.termConditionConsentInfo?.hasRecords ?? false;
      this.termsConditionsAccepted = this.userContext.termConditionConsentInfo?.termsConditionsAccepted ?? false;
      this.electronicConsentAccepted = this.userContext.termConditionConsentInfo?.electronicConsentAccepted ?? false;
      const hasAccepted = this.hasTermsRecords && this.termsConditionsAccepted && this.electronicConsentAccepted;
      this.showPopUpPage = !hasAccepted;
    }
  }

  doneAcceptence() {
    this.showPopUpPage = false;
    if (this.tokenForOverview && this.isSponsor) {
      this.loading = true;
      this.routeToOverview(this.tokenForOverview);
    }
  }

  logout() {
    this.tokenForOverview = '';
    this.showPopUpPage = false;
    this.authService.logout();
  }

  checkGOUser(input: UserContext) {    
    const userType = input.symClass.toLowerCase();  
    const isSponsor = userType === 'sponsor';
    const isTPA = userType === 'tpa';
    const groupId = input.policyholderInfo?.groupId ?? '';
    if (isTPA || (isSponsor && (groupId === 'none' || groupId === ''))) {
      const isIcap = environment.icapEnabled ?? false;
      if(isIcap){
        this.isGoUser = true;
      }
    }
  }

  routeToGo() {
    const LOGIN_FLAG = 'BENEFITS_LOGIN_FLAG';
    sessionStorage.removeItem(LOGIN_FLAG);
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = `${environment.appBaseUrl}/v1/oauth2/togo?goUrl=${environment.goUrl}`;
  }

  isGroupsPage(input: UserContext) {
    const userType = input.symClass.toLowerCase();
    const employeeInfo = input.symEmployeeInfo;
    this.isSymEmpl = userType === 'employee';
    this.isBroker = userType === 'distributor';
    this.isSponsor = userType === 'sponsor';
    const isBasic = employeeInfo?.isBasic;
    const isBasicSymEmp = this.isSymEmpl && isBasic;
    if (isBasicSymEmp || this.isBroker) {
      this.bodyHeader = this.GroupBodyHeader;
      this.isPolicy = false;
      this.pageTitle = this.GroupTitle;
      this.tableName = this.GroupName;
      this.tableNumber = this.GroupNumber;
    }
  }

  routeToOverview(token: string | undefined) {
    const queryParams: NavigationExtras = { queryParams: { token } };
    this.router.navigate([token ? '/benefits/dashboard' : '/benefits/search'], queryParams);
  }

  onClickTableHeader(sortField: string) {
    const newSortDirection =
      this.searchParameter.sortDirection === SearchSortDirection.Descending
        ? SearchSortDirection.Ascending
        : SearchSortDirection.Descending;
    const field: SearchSortField = SearchSortField[sortField as keyof typeof SearchSortField];

    this.sortDirection$.next(newSortDirection);
    this.sortField$.next(field);
  }

  onFilterChange(value: string, filterType: string) {
    if (filterType === 'groupName') {
      this.groupName$.next(value);
    } else if (filterType === 'groupNumber') {
      this.groupNumber$.next(value);
    }
  }

  clearFilters() {
    this.formGroup.setValue({
      groupName: Defaults.stringEmpty,
      groupNumber: Defaults.stringEmpty
    });
  }

  onPageChange(page: number) {
    this.page$.next(page);
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize$.next(pageSize);
  }

  private transformData(response: PolicySearchResultsResponse | undefined): PolicyData {
    const responseData = response?.searchResults ?? [];
    const totalResults = response?.totalResults ?? 0;
    this.showTermsConditionsElectronicPopup();

    if (totalResults === 1 && this.isSponsor) {
      this.tokenForOverview = responseData[0]?.policyToken ?? '';
      if (!this.showPopUpPage && this.tokenForOverview) {
        this.loading = true;
        this.routeToOverview(this.tokenForOverview);
        return this.policyData;
      }
    } else if (totalResults === 0 && this.isSponsor) {
      this.hasPolicies$.next(false);
      this.loading = false;
    }
    const policies: PolicyInfo[] = responseData.map((rsp) => ({
      policyholder: rsp.policyholder,
      policyNumber: rsp.policyId,
      policyToken: rsp.policyToken,
      groupId: rsp.groupId,
      groupName: rsp.groupName,
      dashboardLink: `benefits/dashboard?token=${rsp.policyToken}`
    }));

    this.hasPolicies$.next(!!policies.length);

    if (!this.isSponsor || this.showPopUpPage) {
      this.loading = false;
    }

    this.policyData = {
      policies: policies ?? [],
      totalResults: response?.totalResults ?? 0
    };
    return this.policyData;
  }
}
